<template>
  <BottomSheetModal 
    v-model="isOpen" 
    :confirmDisabled="!isFormValid || isSaving"
    :hideHeader="currentView !== 'main'"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ $t('registerDependent.title') }}</template>

    <div class="views-container">
      <!-- Main View -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">

        <!-- Success state -->
        <div v-if="success" class="success-state">
          <IosSection form>
            <div class="success-content">
              <v-icon size="56" color="success" class="mb-3">mdi-check-circle-outline</v-icon>
              <div class="success-title">{{ $t('registerDependent.success') }}</div>
              <div class="success-subtitle">{{ $t('registerDependent.successDetail') }}</div>
            </div>
          </IosSection>

          <div style="padding: 0 20px 20px">
            <button class="ios-btn ios-btn--primary" @click="resetForm">
              <v-icon size="18" class="mr-2">mdi-repeat-variant</v-icon>
              {{ $t('registerDependent.createAnother') }}
            </button>
            <div style="height: 12px" />
            <button class="ios-btn ios-btn--secondary" @click="isOpen = false">
              {{ $t('common.close') }}
            </button>
          </div>
        </div>

        <!-- Form -->
        <template v-else>
          <!-- Osobní údaje -->
          <IosSection form :title="$t('registerDependent.personalInfo')">
            <IosInput 
              v-model="form.firstName" 
              :placeholder="$t('registerDependent.namePlaceholder')"
              first
            />
            <IosInput 
              v-model="form.lastName" 
              :placeholder="$t('registerDependent.surnamePlaceholder')"
              last
            />
          </IosSection>

          <!-- Doplňující údaje -->
          <IosSection form :title="$t('registerDependent.additionalInfo')">
            <IosRow 
              :label="$t('registerDependent.birthDate')"
              :value="form.birth || ''"
              clickable
              first
              @click="currentView = 'birth'"
            />
            <IosRow 
              :label="$t('registerDependent.location')" 
              :value="form.city?.label || ''"
              clickable
              @click="currentView = 'location'"
            />
            <IosRow 
              :label="$t('registerDependent.preferredPosition')"
              :value="positionDisplay"
              clickable
              last
              @click="currentView = 'position'"
            />
          </IosSection>

          <!-- Herní data -->
          <IosSection form :title="$t('registerDependent.gameData')">
            <IosInput 
              v-model="form.no" 
              :placeholder="$t('registerDependent.jerseyNumber')"
              first
            />
            <IosInput 
              v-model="form.rating" 
              :placeholder="$t('registerDependent.ratingPlaceholder')"
              last
            />
          </IosSection>

          <!-- Přiřadit do týmu -->
          <IosSection v-if="teams.length > 0" form :title="$t('registerDependent.assignToTeam')">
            <IosRow 
              :label="$t('registerDependent.playerTeams')"
              :value="teamDisplay"
              clickable
              first
              last
              @click="currentView = 'teams'"
            />
          </IosSection>

          <!-- Soukromí -->
          <IosSection form :title="$t('account.privacy')">
            <IosRow
              :label="$t('account.hideName')"
              :subtitle="$t('account.hideNameDesc')"
              :value="nameVisibilityLabel"
              clickable
              first
              @click="currentView = 'nameVisibility'"
            />
            <IosRow
              :label="$t('account.contactVisibility')"
              :subtitle="$t('account.contactVisibilityDesc')"
              :value="contactVisibilityLabel"
              clickable
              last
              @click="currentView = 'contactVisibility'"
            />
          </IosSection>

          <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
        </template>
      </div>

      <!-- Birth Date View -->
      <Transition name="slide">
        <div v-if="currentView === 'birth'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('registerDependent.birthDate') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <div class="date-picker-wrapper">
                <input
                  v-model="form.birth"
                  type="date"
                  class="date-input"
                />
              </div>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Location Search View -->
      <Transition name="slide">
        <div v-if="currentView === 'location'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('registerDependent.locationShort') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <div class="search-input-wrapper-ios">
                <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
                <input 
                  v-model="locationSearch"
                  type="text"
                  class="search-input"
                  :placeholder="$t('registerDependent.searchCity')"
                  @input="onLocationSearch"
                />
                <v-progress-circular v-if="searchingCity" indeterminate size="18" width="2" class="ml-2" />
              </div>
            </IosSection>
            <IosSection form v-if="cityResults.length > 0">
              <IosRow 
                v-for="(city, index) in cityResults" 
                :key="city.id"
                :label="city.label"
                :subtitle="city.subtitle"
                :first="index === 0"
                :last="index === cityResults.length - 1"
                clickable
                @click="selectCity(city)"
              />
            </IosSection>
            <div v-else-if="locationSearch.length >= 2 && !searchingCity && cityResults.length === 0" class="search-empty">
              {{ $t('registerDependent.noResults') }}
            </div>
          </div>
        </div>
      </Transition>

      <!-- Position Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'position'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('registerDependent.preferredPosition') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(opt, index) in positionOptions" 
                :key="opt.value"
                :label="opt.text"
                :first="index === 0"
                :last="index === positionOptions.length - 1"
                clickable
                :showChevron="false"
                @click="togglePosition(opt.value)"
              >
                <template #value>
                  <v-icon v-if="form.positions.includes(opt.value)" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Teams Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'teams'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('registerDependent.assignToTeam') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(team, index) in teams" 
                :key="team._id"
                :label="team.name"
                :first="index === 0"
                :last="index === teams.length - 1"
                clickable
                :showChevron="false"
                @click="toggleTeam(team)"
              >
                <template #icon>
                  <v-avatar size="28">
                    <v-img :src="getTeamImage(team.imageUrl || '')" alt="team logo" />
                  </v-avatar>
                </template>
                <template #value>
                  <v-icon v-if="isTeamSelected(team._id)" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Name Visibility View -->
      <Transition name="slide">
        <div v-if="currentView === 'nameVisibility'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('account.hideName') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(opt, index) in visibilityOptions"
                :key="opt.value"
                :label="opt.label"
                :subtitle="opt.subtitle"
                :first="index === 0"
                :last="index === visibilityOptions.length - 1"
                clickable
                :showChevron="false"
                @click="form.nameVisibility = opt.value; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.nameVisibility === opt.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Contact Visibility View -->
      <Transition name="slide">
        <div v-if="currentView === 'contactVisibility'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('account.contactVisibility') }}</div>
            <div class="spacer"></div>
          </div>
          <div class="subview-scroll">
            <IosSection form>
              <IosRow
                v-for="(opt, index) in visibilityOptions"
                :key="opt.value"
                :label="opt.label"
                :subtitle="opt.subtitle"
                :first="index === 0"
                :last="index === visibilityOptions.length - 1"
                clickable
                :showChevron="false"
                @click="form.contactVisibility = opt.value; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.contactVisibility === opt.value" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>
    </div>
  </BottomSheetModal>

  <!-- Duplicate player confirmation -->
  <CenteredModal v-model="showDuplicateConfirm" :show-close-button="true" max-width="350px">
    <div class="delete-confirm-dialog">
      <div class="delete-confirm-icon">
        <v-icon size="48" color="warning">mdi-alert-outline</v-icon>
      </div>
      <h3 class="delete-confirm-title">{{ $t('registerDependent.playerExists') }}</h3>
      <p class="delete-confirm-subtitle">
        {{ $t('registerDependent.playerExistsDetail') }}
        <template v-if="duplicateTeamNames.length">
          Hraje v týmech: <strong>{{ duplicateTeamNames.join(', ') }}</strong>.
        </template>
        {{ $t('registerDependent.playerExistsConfirm') }}
      </p>
      <div class="delete-confirm-actions">
        <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="showDuplicateConfirm = false">
          {{ $t('common.cancel') }}
        </button>
        <button
          class="delete-confirm-btn delete-confirm-btn--confirm"
          :disabled="isSaving"
          @click="confirmDuplicateSave"
        >
          <v-progress-circular v-if="isSaving" indeterminate size="16" width="2" />
          <template v-else>{{ $t('registerDependent.addAnyway') }}</template>
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { PositionEnum } from '@/enums/position.enum'
import axiosInstance from '@/services/axiosInstance'
import { getTeamImage } from '@/utils/imageUtils'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import CenteredModal from '@/components/CenteredModal.vue'
import { IosSection, IosRow, IosInput } from '@/components/ios'
import { useUserStore } from '@/stores/user/userStore'

const userStore = useUserStore()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || 'http://localhost:2000'
const { t } = useI18n({ useScope: 'global' })

type ViewType = 'main' | 'birth' | 'location' | 'position' | 'teams' | 'nameVisibility' | 'contactVisibility'

interface CityOption {
  id: number
  label: string
  subtitle: string
  lat: string
  lon: string
  raw: any
}

interface TeamOption {
  _id: string
  name: string
  imageUrl?: string
}

interface FormState {
  firstName: string
  lastName: string
  birth: string
  city: CityOption | null
  positions: string[]
  no: string
  rating: string
  selectedTeams: TeamOption[]
  nameVisibility: string
  contactVisibility: string
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  prefillTeamId: {
    type: String,
    default: undefined
  },
  prefillPosition: {
    type: String as () => 'goalie' | 'player' | undefined,
    default: undefined
  },
  prefillRole: {
    type: String as () => 'goalkeepers' | 'players' | undefined,
    default: undefined
  },
  prefillFirstName: {
    type: String,
    default: ''
  },
  prefillLastName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'saved', data: { _id: string; firstName: string; lastName: string; name: string; rating?: number }): void
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const currentView = ref<ViewType>('main')
const isSaving = ref(false)
const success = ref(false)
const errorMessage = ref<string | null>(null)
const showDuplicateConfirm = ref(false)
const duplicateTeamNames = ref<string[]>([])
let pendingRegistrationData: any = null

// Teams loaded from API
const teams = ref<TeamOption[]>([])

const form = ref<FormState>({
  firstName: '',
  lastName: '',
  birth: '',
  city: null,
  positions: ['forward', 'defence'],
  no: '',
  rating: '',
  selectedTeams: [],
  nameVisibility: 'public',
  contactVisibility: 'teamStaff',
})

// Position options
const positionOptions = Object.entries(PositionEnum).map(([key, value]) => ({
  value: key.toLowerCase(),
  text: value as string,
}))

// Display values
const positionDisplay = computed(() => {
  if (form.value.positions.length === 0) return ''
  return form.value.positions
    .map(p => positionOptions.find(o => o.value === p)?.text || p)
    .join(', ')
})

const teamDisplay = computed(() => {
  if (form.value.selectedTeams.length === 0) return ''
  return form.value.selectedTeams.map(t => t.name).join(', ')
})

// Visibility options (sjednocené pro jméno i kontakty)
const visibilityOptions = computed(() => [
  { value: 'public', label: t('account.visibility.public'), subtitle: t('account.visibility.publicDesc') },
  { value: 'sameLeague', label: t('account.visibility.sameLeague'), subtitle: t('account.visibility.sameLeagueDesc') },
  { value: 'teammates', label: t('account.visibility.teammates'), subtitle: t('account.visibility.teammatesDesc') },
  { value: 'teamStaff', label: t('account.visibility.teamStaff'), subtitle: t('account.visibility.teamStaffDesc') },
])
const nameVisibilityLabel = computed(() =>
  visibilityOptions.value.find(o => o.value === form.value.nameVisibility)?.label ?? t('account.visibility.public')
)
const contactVisibilityLabel = computed(() =>
  visibilityOptions.value.find(o => o.value === form.value.contactVisibility)?.label ?? t('account.visibility.teamStaff')
)

// Validation
const isFormValid = computed(() => {
  return form.value.firstName.trim().length >= 2 &&
    form.value.lastName.trim().length >= 2
})

// === Position toggle ===
function togglePosition(value: string) {
  const idx = form.value.positions.indexOf(value)
  if (idx > -1) {
    form.value.positions.splice(idx, 1)
  } else {
    form.value.positions.push(value)
  }
}

// === Team toggle ===
function toggleTeam(team: TeamOption) {
  const idx = form.value.selectedTeams.findIndex(t => t._id === team._id)
  if (idx > -1) {
    form.value.selectedTeams.splice(idx, 1)
  } else {
    form.value.selectedTeams.push(team)
  }
}

function isTeamSelected(id: string) {
  return form.value.selectedTeams.some(t => t._id === id)
}

// === Location search ===
const locationSearch = ref('')
const cityResults = ref<CityOption[]>([])
const searchingCity = ref(false)
let citySearchTimer: number | undefined

function onLocationSearch() {
  const q = locationSearch.value.trim()
  if (q.length < 2) { cityResults.value = []; return }
  if (citySearchTimer) window.clearTimeout(citySearchTimer)
  citySearchTimer = window.setTimeout(() => searchCities(q), 300)
}

async function searchCities(query: string) {
  searchingCity.value = true
  try {
    const url = new URL('https://nominatim.openstreetmap.org/search')
    url.searchParams.set('q', query)
    url.searchParams.set('format', 'jsonv2')
    url.searchParams.set('addressdetails', '1')
    url.searchParams.set('limit', '10')
    url.searchParams.set('featuretype', 'city')
    const res = await fetch(url.toString(), { headers: { 'Accept': 'application/json' } })
    const data = await res.json()
    const mapped: CityOption[] = (Array.isArray(data) ? data : []).map((r: any) => {
      const addr = r.address || {}
      const cityLike = addr.city || addr.town || addr.village || r.display_name?.split(',')?.[0] || r.name
      const region = addr.state || addr.region || addr.county
      const country = addr.country || 'Česko'
      return {
        id: Number(r.place_id),
        label: String(cityLike),
        subtitle: [region, country].filter(Boolean).join(', '),
        lat: r.lat,
        lon: r.lon,
        raw: r,
      }
    })
    const seen = new Set<string>()
    cityResults.value = mapped.filter(m => {
      const key = m.label.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  } catch (e) {
    console.error('City search failed:', e)
    cityResults.value = []
  } finally {
    searchingCity.value = false
  }
}

function selectCity(city: CityOption) {
  form.value.city = city
  currentView.value = 'main'
}

// === Fetch managed teams ===
async function fetchManagedTeams() {
  try {
    const { data } = await axiosInstance.get('/teams/myteams', {
      params: { roles: 'admins,coaches' },
    })
    const managed = Array.isArray(data?.teams) ? data.teams : []
    teams.value = managed.map((t: any) => ({
      _id: String(t._id),
      name: t.name,
      imageUrl: t.imageUrl || null,
    }))
  } catch (e) {
    console.error('Chyba při načítání spravovaných týmů:', e)
    teams.value = []
  }
}

// === Save ===
async function handleSave() {
  if (isSaving.value || success.value) return

  // Validate
  if (form.value.firstName.trim().length < 2) {
    errorMessage.value = t('registerDependent.nameMinLength')
    return
  }
  if (form.value.lastName.trim().length < 2) {
    errorMessage.value = t('registerDependent.surnameMinLength')
    return
  }
  if (form.value.no && form.value.no.length > 3) {
    errorMessage.value = t('registerDependent.jerseyMaxLength')
    return
  }
  if (form.value.rating) {
    const r = Number(form.value.rating)
    if (isNaN(r) || r < 0 || r > 10) {
      errorMessage.value = t('registerDependent.ratingRange')
      return
    }
  }
  const name = `${form.value.firstName.trim().charAt(0)}. ${form.value.lastName.trim()}`
  const teamIds = form.value.selectedTeams.map(tm => tm._id)

  const registrationData = {
    firstName: form.value.firstName.trim(),
    lastName: form.value.lastName.trim(),
    name,
    email: '',
    password: '',
    birth: form.value.birth || undefined,
    phone: '',
    preferredPosition: form.value.positions,
    playLeague: false,
    _teams: teamIds,
    no: form.value.no || undefined,
    rating: form.value.rating || undefined,
    role: props.prefillRole || undefined,
    nameVisibility: form.value.nameVisibility,
    contactVisibility: form.value.contactVisibility,
    location: form.value.city
      ? {
          city: form.value.city.label,
          region: form.value.city.raw?.address?.state || null,
          country: form.value.city.raw?.address?.country || 'Česko',
          lat: Number(form.value.city.lat),
          lon: Number(form.value.city.lon),
        }
      : null,
  }

  errorMessage.value = null
  isSaving.value = true

  try {
    const { data: responseData } = await axiosInstance.post(`${API_BASE_URL}/users/register-dependant`, registrationData)
    // Remember city & teams for next registration
    lastCity = form.value.city
    lastTeams = [...form.value.selectedTeams]
    success.value = true
    emit('saved', {
      _id: responseData.user?._id || '',
      firstName: form.value.firstName.trim(),
      lastName: form.value.lastName.trim(),
      name,
      rating: form.value.rating ? Number(form.value.rating) : undefined,
    })
  } catch (error: any) {
    if (error?.response?.status === 409 && error?.response?.data?.duplicate) {
      // Duplicate found — show confirmation dialog
      duplicateTeamNames.value = error.response.data.teamNames || []
      pendingRegistrationData = { ...registrationData, force: true }
      showDuplicateConfirm.value = true
    } else {
      errorMessage.value = error?.response?.data?.message || t('registerDependent.submitError')
      console.error('Error submitting registration:', error)
    }
  } finally {
    isSaving.value = false
  }
}

async function confirmDuplicateSave() {
  if (isSaving.value || !pendingRegistrationData) return
  isSaving.value = true
  try {
    const { data: responseData } = await axiosInstance.post(`${API_BASE_URL}/users/register-dependant`, pendingRegistrationData)
    lastCity = form.value.city
    lastTeams = [...form.value.selectedTeams]
    success.value = true
    showDuplicateConfirm.value = false
    const name = `${form.value.firstName.trim().charAt(0)}. ${form.value.lastName.trim()}`
    emit('saved', {
      _id: responseData.user?._id || '',
      firstName: form.value.firstName.trim(),
      lastName: form.value.lastName.trim(),
      name,
      rating: form.value.rating ? Number(form.value.rating) : undefined,
    })
  } catch (error: any) {
    errorMessage.value = error?.response?.data?.message || t('registerDependent.submitError')
    console.error('Error submitting registration:', error)
    showDuplicateConfirm.value = false
  } finally {
    isSaving.value = false
    pendingRegistrationData = null
  }
}

// Remember city & teams from previous submission
let lastCity: CityOption | null = null
let lastTeams: TeamOption[] = []

// === Reset ===
function resetForm() {
  const parentNv = (userStore.user as any)?.nameVisibility ?? 'public'
  const parentCv = (userStore.user as any)?.contactVisibility ?? 'teamStaff'
  form.value = {
    firstName: '',
    lastName: '',
    birth: '',
    city: lastCity,
    positions: ['forward', 'defence'],
    no: '',
    rating: '',
    selectedTeams: [...lastTeams],
    nameVisibility: parentNv,
    contactVisibility: parentCv,
  }
  success.value = false
  errorMessage.value = null
  isSaving.value = false
  currentView.value = 'main'
}

function handleClose() {
  currentView.value = 'main'
}

// Load teams when modal opens
watch(isOpen, async (val) => {
  if (val) {
    lastCity = null
    lastTeams = []
    resetForm()

    // Prefill name from search query
    if (props.prefillFirstName) form.value.firstName = props.prefillFirstName
    if (props.prefillLastName) form.value.lastName = props.prefillLastName

    await fetchManagedTeams()

    // Prefill position based on context (goalie vs player)
    if (props.prefillPosition === 'goalie') {
      form.value.positions = ['goalie']
    } else if (props.prefillPosition === 'player') {
      form.value.positions = ['forward', 'defence']
    }

    // Prefill team if provided
    if (props.prefillTeamId) {
      const existing = teams.value.find(t => t._id === props.prefillTeamId)
      if (existing) {
        form.value.selectedTeams = [existing]
        // Fetch full team data for location
        try {
          const { data } = await axiosInstance.get(`/teams/${props.prefillTeamId}`)
          if (data?.location && typeof data.location === 'object' && data.location.city) {
            form.value.city = {
              id: 0,
              label: data.location.city || data.location.label || '',
              subtitle: data.location.subtitle || '',
              lat: data.location.lat || '',
              lon: data.location.lon || '',
              raw: data.location,
            }
          }
        } catch (e) {
          // Location prefill is optional
        }
      } else {
        // Team not in managed list — fetch it directly
        try {
          const { data } = await axiosInstance.get(`/teams/${props.prefillTeamId}`)
          if (data?._id) {
            const team: TeamOption = {
              _id: String(data._id),
              name: data.name,
              imageUrl: data.imageUrl || undefined,
            }
            if (!teams.value.some(t => t._id === team._id)) {
              teams.value.push(team)
            }
            form.value.selectedTeams = [team]

            // Prefill location from team
            if (data.location && typeof data.location === 'object' && data.location.city) {
              form.value.city = {
                id: 0,
                label: data.location.city || data.location.label || '',
                subtitle: data.location.subtitle || '',
                lat: data.location.lat || '',
                lon: data.location.lon || '',
                raw: data.location,
              }
            }
          }
        } catch (e) {
          console.error('Failed to fetch prefill team:', e)
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>
// Views container
.views-container {
  position: relative;
  overflow: hidden;
  min-height: calc(92vh - 90px);
}

.main-view {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), 
              opacity 0.35s ease;
  
  &--hidden {
    transform: translateX(-30%);
    opacity: 0;
    pointer-events: none;
  }
}

// Slide transition - iOS style
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText));
  }
  
  &:active {
    transform: scale(0.92);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

// Search
.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  
  .search-icon {
    color: rgba(var(--v-theme-sheetText), 0.5);
    margin-right: 12px;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: rgb(var(--v-theme-sheetText));
    
    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.35);
    }
  }
}

.search-empty {
  text-align: center;
  padding: 32px 16px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.95rem;
}

// Date picker
.date-picker-wrapper {
  padding: 16px;
}

.date-input {
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  font-size: 16px;
  color: rgb(var(--v-theme-sheetText));
  padding: 4px 0;
  -webkit-appearance: none;
  appearance: none;
  color-scheme: dark;

  .v-theme--light & {
    color-scheme: light;
  }
}

// Success
.success-state {
  padding-top: 20px;
}

.success-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px;
  text-align: center;
}

.success-title {
  font-size: 18px;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-bottom: 4px;
}

.success-subtitle {
  font-size: 14px;
  color: rgb(var(--v-theme-sheetText));
  opacity: 0.6;
}

// Error
.error-message {
  color: #d32f2f;
  font-size: 13px;
  text-align: center;
  margin-top: 12px;
  padding: 0 20px 20px;
}

// Buttons
.ios-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:active { opacity: 0.7; }
  &:disabled { opacity: 0.5; }

  &--primary {
    background: rgb(var(--v-theme-brand));
    color: #ffffff;
  }

  &--secondary {
    background: rgba(var(--v-theme-sheetText), 0.08);
    color: rgb(var(--v-theme-sheetText));
  }
}

/* ── CenteredModal confirm dialog (teleported to body) ── */
</style>

<style lang="scss">
.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}

.delete-confirm-icon {
  margin-bottom: 16px;
}

.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 24px;
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }

  &--confirm {
    background: rgb(var(--v-theme-ice));
    color: white;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}
</style>
